import React, { useRef, useState } from "react";
import { motion } from "framer-motion";
import { cn } from "@/lib/utils";

// BlurText — word-by-word blur-in reveal (21st.dev-style)
export function BlurText({ text, className, delay = 0, stagger = 0.09, as = "h1", ...rest }) {
  const MotionTag = motion[as] || motion.h1;
  const words = text.split(" ");
  return (
    <MotionTag
      className={className}
      initial="hidden"
      animate="show"
      variants={{ show: { transition: { staggerChildren: stagger, delayChildren: delay } } }}
      {...rest}
    >
      {words.map((w, i) => (
        <motion.span
          key={`${w}-${i}`}
          className="inline-block"
          variants={{
            hidden: { opacity: 0, filter: "blur(12px)", y: "0.4em" },
            show: { opacity: 1, filter: "blur(0px)", y: 0, transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] } },
          }}
        >
          {w}
          {i < words.length - 1 ? "\u00A0" : ""}
        </motion.span>
      ))}
    </MotionTag>
  );
}

// FloatIcon — gentle idle bob + hover tilt for 3D icons (21st.dev-style)
export function FloatIcon({ src, alt, className, delay = 0 }) {
  return (
    <motion.img
      src={src}
      alt={alt}
      className={className}
      animate={{ y: [0, -9, 0] }}
      transition={{ duration: 3.6, repeat: Infinity, ease: "easeInOut", delay }}
      whileHover={{ scale: 1.14, rotate: -5, transition: { type: "spring", stiffness: 280, damping: 12 } }}
    />
  );
}

// SpotlightCard — radial glow that follows the cursor (21st.dev-style)
export function SpotlightCard({ children, className, contentClassName, glow = "rgba(5,150,105,0.25)", "data-testid": testId }) {
  const ref = useRef(null);
  const [pos, setPos] = useState({ x: -200, y: -200 });
  const [active, setActive] = useState(false);

  const onMove = (e) => {
    const r = ref.current?.getBoundingClientRect();
    if (!r) return;
    setPos({ x: e.clientX - r.left, y: e.clientY - r.top });
  };

  return (
    <div
      ref={ref}
      data-testid={testId}
      onMouseMove={onMove}
      onMouseEnter={() => setActive(true)}
      onMouseLeave={() => setActive(false)}
      className={cn("group relative overflow-hidden", className)}
    >
      <div
        className="pointer-events-none absolute inset-0 transition-opacity duration-300"
        style={{
          opacity: active ? 1 : 0,
          background: `radial-gradient(220px circle at ${pos.x}px ${pos.y}px, ${glow}, transparent 70%)`,
        }}
      />
      <div className={cn("relative h-full", contentClassName)}>{children}</div>
    </div>
  );
}
